import { PageHeader, Button, Card, CardContent, Skeleton } from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Save } from "@/components/icons";
import { TemplateEditor } from "@/components/theme-editor";
import i18n from "@/i18n";
import { getTemplate, saveTemplate } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import {
  draftToSaveTemplateReq,
  templateToDraft,
  type TemplateDraft,
} from "@/lib/templates";
import type { GetTemplateResp } from "@/lib/types";

export function EditTemplatePage() {
  const { t } = useTranslation();
  const { templateId = "" } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<TemplateDraft | null>(null);

  const query = useQuery<GetTemplateResp>({
    queryKey: ["template", templateId],
    queryFn: () => getTemplate({ id: templateId }),
    enabled: templateId.length > 0,
  });

  useEffect(() => {
    if (query.data) setDraft(templateToDraft(query.data));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error(i18n.t("templates.notLoaded"));
      return saveTemplate(draftToSaveTemplateReq(draft, templateId));
    },
    onSuccess: async (resp) => {
      if (resp.warnings.length > 0) {
        toast.warning(t("templates.savedWithWarnings"));
      } else {
        toast.success(t("templates.saved"));
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["templates"] }),
        queryClient.invalidateQueries({ queryKey: ["template", templateId] }),
      ]);
      navigate("/templates");
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  if (query.isError) {
    return <p className="text-sm text-destructive">{t("templates.loadOneFailed")}</p>;
  }

  if (query.isLoading || !draft) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const canSave = draft.name.trim().length > 0 && !mutation.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("templates.title")}
        backTo="/templates"
        backLabel={t("templates.backToTemplates")}
        title={t("templates.editTitle")}
        subtitle={query.data?.name}
        actions={
          <Button disabled={!canSave} onClick={() => mutation.mutate()}>
            {mutation.isPending ? (
              t("common.saving")
            ) : (
              <>
                <Save className="h-4 w-4" />
                {t("common.save")}
              </>
            )}
          </Button>
        }
      />
      <Card>
        <CardContent className="pt-6">
          <TemplateEditor value={draft} onChange={setDraft} />
        </CardContent>
      </Card>
    </div>
  );
}
